/**
 * Einwilligung für die Google-Ads-Messung (Datenschutz „Google Ads Conversion-Tracking“, Vertrieb 27.10.2026).
 *
 * Nur mit Opt-in: Bis zur Zustimmung lädt kein Google-Skript und kein Google-Cookie entsteht. Die Wahl steht im Cookie
 * co_einwilligung auf .creatoroffice.de (technisch notwendig, 6 Monate) und gilt damit für Website und App gemeinsam;
 * apps/website/assets/site.js liest und schreibt dasselbe Cookie. Consent Mode v2: Standard alles „denied“, nach
 * Zustimmung ad_storage, ad_user_data und ad_personalization „granted“ (Messung und Remarketing, Inhaber 27.10.2026), keine Analyse.
 *
 * Das Google-Tag lädt nur auf app.creatoroffice.de (nie in Vorschauen, Staging oder lokal), damit Testkonten die Zahlen in
 * Google Ads nicht verfälschen. Die Conversion „Registrierung“ geht einmal je neuem Konto: transaction_id ist die Kennung
 * des Kontos (Google zählt dieselbe Kennung nicht zweimal), zusätzlich merkt sich der Browser gemeldete Konten.
 * Mit derselben Zustimmung lädt das Meta-Pixel („CreatorOffice Website“, Portfolio CreatorOffice, Vertrieb 27.10.2026): PageView beim Laden,
 * CompleteRegistration an derselben Stelle wie die Google-Conversion, eventID ebenfalls die Kennung des Kontos.
 * Nach der Rückkehr aus Stripe oder PayPal (Inhaber 02.11.2026): läuft noch der Test, „Test mit Karte“ mit Wert 1 € (Meta „StartTrial“,
 * Kennung „test-<Konto>“), denn bezahlt ist noch nichts; ist das Abo schon bezahlt (Kauf ohne Test), „Abo abgeschlossen“ mit dem Preis
 * des ersten Zeitraums (Meta „Subscribe“, Kennung „abo-<Konto>“). Gleiche Regeln wie oben.
 */

export type Wahl = "ja" | "nein";

const COOKIE = "co_einwilligung";
const TAG = "AW-18539950208";
const REGISTRIERUNG = "AW-18539950208/N0-SCPyNpasdEIDhxIhF";
const ABO = "AW-18539950208/Lvt-CLuf0bAdEIDhxIhF";
const GEMELDET = "co-conversion-registrierung";
const ABO_GEMELDET = "co-conversion-abo";
const TEST = "AW-18539950208/EhaTCLT72LAdEIDhxIhF";
const TEST_GEMELDET = "co-conversion-test";
const MAX_AGE = 15552000; // 180 Tage
const PIXEL = "2030862830953541";

type Fbq = ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue: unknown[]; push?: unknown; loaded?: boolean; version?: string };
type Fenster = Window & { dataLayer?: unknown[]; fbq?: Fbq; _fbq?: Fbq };

function domainTeil(host: string): string {
  return /(^|\.)creatoroffice\.de$/.test(host) ? "; Domain=.creatoroffice.de" : "";
}

/**
 * Banner und Google-Tag nur auf Anmelden, Registrieren und Einrichtung (Prüf-Chat C-1, Datensparsamkeit): nicht auf
 * /passwort, /bestaetigen, /zahlungsmethode, Tarifwahl oder Sperrseite, obwohl diese denselben Rahmen nutzen.
 */
export function seiteMitEinwilligung(pfad: string): boolean {
  return /^\/(anmelden|registrieren|einrichtung)(\/|$)/.test(pfad);
}

/** Nur die echte App lädt das Google-Tag */
export function tagErlaubt(host: string = location.hostname): boolean {
  return host === "app.creatoroffice.de";
}

export function wahlAusCookie(cookie: string): Wahl | null {
  const m = cookie.match(/(?:^|;\s*)co_einwilligung=v1\.(werbung|abgelehnt)(?:;|$)/);
  return m ? (m[1] === "werbung" ? "ja" : "nein") : null;
}

export function leseWahl(): Wahl | null {
  try {
    return wahlAusCookie(document.cookie);
  } catch {
    return null;
  }
}

export function cookieFuer(wahl: Wahl, host: string, https: boolean): string {
  return `${COOKIE}=v1.${wahl === "ja" ? "werbung" : "abgelehnt"}; Max-Age=${MAX_AGE}; Path=/; SameSite=Lax${domainTeil(host)}${https ? "; Secure" : ""}`;
}

const hoerer = new Set<() => void>();
export function aufWahlHoeren(fn: () => void): () => void {
  hoerer.add(fn);
  return () => hoerer.delete(fn);
}

/** Banner erneut zeigen („Cookie-Einstellungen“) */
let offen = false;
export function einstellungenOffen(): boolean {
  return offen;
}
export function einstellungenOeffnen(): void {
  offen = true;
  hoerer.forEach((fn) => fn());
}

function gtag(..._args: unknown[]): void {
  const w = window as Fenster;
  w.dataLayer = w.dataLayer || [];
  // gtag erwartet das arguments-Objekt, kein Array
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments);
}

function fbq(...args: unknown[]): void {
  (window as Fenster).fbq?.(...args);
}

/** Meta-Pixel (Standard-Ladeschnipsel von Meta, ausgeschrieben); nur aus ladeGoogleTag, also mit Zustimmung */
function ladeMetaPixel(): void {
  const w = window as Fenster;
  if (!w.fbq) {
    const f = function (this: unknown, ...args: unknown[]) {
      if (f.callMethod) f.callMethod(...args);
      else f.queue.push(args);
    } as Fbq;
    f.queue = [];
    f.push = f;
    f.loaded = true;
    f.version = "2.0";
    w.fbq = f;
    if (!w._fbq) w._fbq = f;
  }
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);
  // Kein automatisches Auslesen von Formularfeldern (erweiterter Abgleich): Datenschutz sagt „Name, E-Mail … übermitteln wir nicht“
  fbq("set", "autoConfig", false, PIXEL);
  fbq("init", PIXEL);
  fbq("track", "PageView");
}

let geladen = false;
/** Google-Tag und Meta-Pixel laden, nur mit Zustimmung und nur auf der echten App */
export function ladeGoogleTag(): void {
  if (geladen || leseWahl() !== "ja" || !tagErlaubt()) return;
  geladen = true;
  gtag("consent", "default", { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied" });
  gtag("consent", "update", { ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted" });
  gtag("js", new Date());
  gtag("config", TAG);
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${TAG}`;
  document.head.appendChild(s);
  ladeMetaPixel();
}

export function waehle(wahl: Wahl): void {
  try {
    document.cookie = cookieFuer(wahl, location.hostname, location.protocol === "https:");
  } catch {
    /* ohne Cookies bleibt alles aus */
  }
  offen = false;
  if (wahl === "ja") {
    // Erneute Zustimmung nach „Ablehnen“ in derselben Seitenansicht: nur freigeben, nicht doppelt laden
    if (geladen) {
      gtag("consent", "update", { ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted" });
      fbq("consent", "grant");
    } else ladeGoogleTag();
  } else widerrufen();
  hoerer.forEach((fn) => fn());
}

/** Widerruf: Google bekommt „denied“, Meta „revoke“, die Cookies von Google (_gcl_*) und Meta (_fbp, _fbc) werden gelöscht */
function widerrufen(): void {
  if (geladen) {
    gtag("consent", "update", { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
    fbq("consent", "revoke");
  }
  try {
    for (const c of document.cookie.split(";")) {
      const name = c.split("=")[0]!.trim();
      if (!/^(_gcl_|_fbp$|_fbc$)/.test(name)) continue;
      document.cookie = `${name}=; Max-Age=0; Path=/${domainTeil(location.hostname)}`;
      document.cookie = `${name}=; Max-Age=0; Path=/`;
    }
  } catch {
    /* nichts zu löschen */
  }
}

function gemeldeteKonten(schluessel: string = GEMELDET): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(schluessel) ?? "[]");
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Conversion „Registrierung“ nach dem Anlegen eines neuen Kontos: nur mit Zustimmung, nur auf der echten App und je
 * Konto höchstens einmal. Gibt zurück, ob gemeldet wurde.
 */
export function meldeRegistrierung(orgId: string): boolean {
  if (!orgId || leseWahl() !== "ja" || !tagErlaubt()) return false;
  const schon = gemeldeteKonten();
  if (schon.includes(orgId)) return false;
  ladeGoogleTag();
  gtag("event", "conversion", { send_to: REGISTRIERUNG, value: 1.0, currency: "EUR", transaction_id: orgId });
  fbq("track", "CompleteRegistration", { value: 1.0, currency: "EUR" }, { eventID: orgId });
  try {
    localStorage.setItem(GEMELDET, JSON.stringify([...schon, orgId].slice(-20)));
  } catch {
    /* transaction_id verhindert die doppelte Zählung auch ohne Speicher */
  }
  return true;
}

/**
 * Conversion „Abo abgeschlossen“ nach der Rückkehr aus Stripe oder PayPal, sobald das Konto als bezahlt gilt: nur mit Zustimmung,
 * nur auf der echten App und je Konto höchstens einmal. Wert ist der Preis des ersten Zeitraums (netto, Euro).
 */
export function meldeAbo(orgId: string, wertEuro: number): boolean {
  if (!orgId || leseWahl() !== "ja" || !tagErlaubt()) return false;
  const schon = gemeldeteKonten(ABO_GEMELDET);
  if (schon.includes(orgId)) return false;
  const value = Number.isFinite(wertEuro) && wertEuro > 0 ? Math.round(wertEuro * 100) / 100 : 1.0;
  ladeGoogleTag();
  gtag("event", "conversion", { send_to: ABO, value, currency: "EUR", transaction_id: `abo-${orgId}` });
  fbq("track", "Subscribe", { value, currency: "EUR" }, { eventID: `abo-${orgId}` });
  try {
    localStorage.setItem(ABO_GEMELDET, JSON.stringify([...schon, orgId].slice(-20)));
  } catch {
    /* transaction_id verhindert die doppelte Zählung auch ohne Speicher */
  }
  return true;
}

/** Conversion „Test mit Karte“: Test mit hinterlegter Zahlungsart, Wert 1 €; gleiche Regeln wie meldeAbo */
export function meldeTestMitKarte(orgId: string): boolean {
  if (!orgId || leseWahl() !== "ja" || !tagErlaubt()) return false;
  const schon = gemeldeteKonten(TEST_GEMELDET);
  if (schon.includes(orgId)) return false;
  ladeGoogleTag();
  gtag("event", "conversion", { send_to: TEST, value: 1.0, currency: "EUR", transaction_id: `test-${orgId}` });
  fbq("track", "StartTrial", { value: 1.0, currency: "EUR" }, { eventID: `test-${orgId}` });
  try {
    localStorage.setItem(TEST_GEMELDET, JSON.stringify([...schon, orgId].slice(-20)));
  } catch {
    /* transaction_id verhindert die doppelte Zählung auch ohne Speicher */
  }
  return true;
}
